import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit} from '@angular/core';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {MatTableDataSource} from '@angular/material/table';
import {RouterModule} from '@angular/router';
import {forkJoin, of, Subject} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, finalize, takeUntil} from 'rxjs/operators';
import {MaterialModule} from '../../../material.module';
import {ConnectWorkflow, ConnectWorkflowsService} from '../../../services/connect/connect-workflows.service';
import {
    ConnectNodeCatalog,
    ConnectNodesService,
    projectConnectGuidedConnectorOperation,
} from '../../../services/connect/connect-nodes.service';
import {ClientStateService} from '../../../services/auth/client-state.service';
import {ConnectGuidedWorkflowJsonAdapterSession} from '../guided/connect-guided-workflow-json-adapter';

@Component({
  selector: 'app-connect-workflows',
  standalone: true,
    imports: [CommonModule, RouterModule, MaterialModule, ReactiveFormsModule],
  templateUrl: './connect-workflows.component.html',
  styleUrls: ['../connect-shared.scss', './connect-workflows.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {class: 'connect-theme'},
})
export class ConnectWorkflowsComponent implements OnInit, OnDestroy {
  private readonly workflowsService = inject(ConnectWorkflowsService);
  private readonly nodesService = inject(ConnectNodesService);
  private readonly clientState = inject(ClientStateService);
  private readonly cdr = inject(ChangeDetectorRef);
    loading = false;
    readonly searchControl = new FormControl<string>('', {nonNullable: true});
  error: string | null = null;
    availableTags: string[] = [];
    selectedTag: string | null = null;
    readonly displayedColumns: string[] = ['name', 'edit'];
    readonly trackByWorkflow = (_index: number, workflow: ConnectWorkflow) => workflow.id ?? workflow.name;
    readonly dataSource = new MatTableDataSource<ConnectWorkflow>([]);
    private readonly destroy$ = new Subject<void>();
    private readonly workflowRoutes = new Map<string, readonly string[] | null>();
    private clientGeneration = 0;

  ngOnInit(): void {
      this.setupFilters();
      this.clientState.resetCache$.pipe(takeUntil(this.destroy$)).subscribe(reset => {
          if (reset) this.loadWorkflows();
      });
    this.loadWorkflows();
  }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
  }

  loadWorkflows(): void {
      const generation = ++this.clientGeneration;
      this.loading = true;
      this.error = null;
      this.dataSource.data = [];
      this.workflowRoutes.clear();
      forkJoin({
          workflows: this.workflowsService.listAllWorkflows(),
          catalog: this.nodesService.fetchCatalog().pipe(catchError(() => of<ConnectNodeCatalog | null>(null))),
      }).pipe(
      catchError(err => {
        if (generation !== this.clientGeneration) return of({workflows: [], catalog: null});
        console.error('Failed to load workflows', err);
        this.error = 'Could not load workflows yet.';
          this.dataSource.data = [];
          this.availableTags = [];
          this.selectedTag = null;
        this.cdr.markForCheck();
        return of<{workflows: ConnectWorkflow[]; catalog: ConnectNodeCatalog | null}>({
            workflows: [],
            catalog: null,
        });
      }),
      finalize(() => {
        if (generation !== this.clientGeneration) return;
          this.loading = false;
        this.cdr.markForCheck();
      })
    ).subscribe(({workflows, catalog}) => {
          if (generation !== this.clientGeneration) return;
          const sorted = [...workflows].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
          sorted.forEach(workflow => this.workflowRoutes.set(
              this.workflowRouteKey(workflow),
              this.resolveWorkflowRoute(workflow, catalog),
          ));
          this.dataSource.data = sorted;
          this.availableTags = this.computeAvailableTags(sorted);
          if (this.selectedTag && !this.availableTags.includes(this.selectedTag)) this.selectedTag = null;
          this.applyFilter();
          this.cdr.markForCheck();
      });
  }

    workflowRoute(workflow: ConnectWorkflow): readonly string[] | null {
        return this.workflowRoutes.get(this.workflowRouteKey(workflow)) ?? null;
    }

    isGuidedUnavailable(workflow: ConnectWorkflow): boolean {
        return this.hasGuidedTag(workflow) && this.workflowRoute(workflow) === null;
    }

    applyTagFilter(tag: string | null): void {
        this.selectedTag = tag;
        this.applyFilter();
    }

    get hasFilteredNoResults(): boolean {
        return this.dataSource.data.length > 0 && this.dataSource.filteredData.length === 0;
    }

    clearFilters(): void {
        this.searchControl.setValue('', {emitEvent: false});
        this.selectedTag = null;
        this.applyFilter();
    }

    getWorkflowDescription(wf: ConnectWorkflow): string {
        return typeof (wf as any).description === 'string' ? String((wf as any).description) : '';
  }

    getWorkflowMetaLine(wf: ConnectWorkflow): string {
        const updated = (wf as any).updatedAt || (wf as any).updated_at || (wf as any).createdAt || (wf as any).created_at;
        if (!updated) return wf.id ? `ID ${wf.id}` : '';
    return `Updated ${updated}`;
  }

    private setupFilters(): void {
        this.dataSource.filterPredicate = (wf, filter) => {
            const parsed = this.parseFilter(filter);
            const selectedTag = parsed.tag;
            const q = parsed.q.trim().toLowerCase();

            if (selectedTag && !this.getWorkflowTags(wf).includes(selectedTag)) return false;

            if (!q) return true;

            const name = String(wf.name ?? '').toLowerCase();
            const id = String(wf.id ?? '').toLowerCase();
            const description = this.getWorkflowDescription(wf).toLowerCase();
            const tags = this.getWorkflowTags(wf).join(' ').toLowerCase();
            return name.includes(q) || id.includes(q) || description.includes(q) || tags.includes(q);
        };

        this.searchControl.valueChanges
            .pipe(debounceTime(200), distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe(() => this.applyFilter());
    }

    private applyFilter(): void {
        this.dataSource.filter = JSON.stringify({q: this.searchControl.value, tag: this.selectedTag});
        this.cdr.markForCheck();
  }

    private parseFilter(filter: string): { q: string; tag: string | null } {
        try {
            const parsed = JSON.parse(filter) as { q?: unknown; tag?: unknown };
            return {
                q: typeof parsed.q === 'string' ? parsed.q : '',
                tag: typeof parsed.tag === 'string' ? parsed.tag : null,
            };
        } catch {
            return {q: String(filter || ''), tag: null};
    }
  }

    private getWorkflowTags(wf: ConnectWorkflow): string[] {
        if (!Array.isArray(wf.tags)) return [];
        return wf.tags.filter((tag): tag is string => typeof tag === 'string' && Boolean(tag.trim())).map(tag => tag.trim());
    }

    private computeAvailableTags(items: ConnectWorkflow[]): string[] {
        const set = new Set<string>();
        items.forEach(wf => this.getWorkflowTags(wf).forEach(tag => set.add(tag)));
        return Array.from(set).sort((a, b) => a.localeCompare(b));
  }

    private resolveWorkflowRoute(
        workflow: ConnectWorkflow,
        catalog: ConnectNodeCatalog | null,
    ): readonly string[] | null {
        if (typeof workflow.id !== 'string' || !workflow.id.trim()) return null;
        if (!this.hasGuidedTag(workflow)) return ['/connect/workflows', workflow.id, 'edit'];
        const projector = catalog
            ? (node: Parameters<typeof projectConnectGuidedConnectorOperation>[0], providerRef: string) =>
                projectConnectGuidedConnectorOperation(node, providerRef, catalog.nodes)
            : undefined;
        return ConnectGuidedWorkflowJsonAdapterSession.open(workflow, projector, catalog?.nodes)
            ? ['/connect/workflows', workflow.id, 'guided']
            : null;
    }

    private hasGuidedTag(workflow: ConnectWorkflow): boolean {
        return this.getWorkflowTags(workflow).some(tag => tag.toLowerCase() === 'guided');
    }

    private workflowRouteKey(workflow: ConnectWorkflow): string {
        return typeof workflow.id === 'string' && workflow.id.trim()
            ? workflow.id
            : `name:${workflow.name}`;
    }
}
